type SanityImageObject = {
	id: string;
	type: string;
	widthHeight: string;
	format: string;
	width: number;
	height: number;
}

type SanityFormatAndSizeParametersOptions = {
	width: number,
	height: number,
	fit: 'clip' | 'crop' | 'fill' | 'fillmax' | 'max' | 'scale' | 'min',
	format: 'auto' | 'jpg' | 'pjpg' | 'png' | 'webp'
}

/**
 * Parses a Sanity image reference string and returns an object
 * containing image details such as ID, dimensions, and format.
 * @param ref - The Sanity image reference string (e.g., "image-id-300x450-jpg").
 * @see {link: https://www.sanity.io/docs/image-url} for more information
 */
export const sanityImageObjectFromRef = (ref: string): SanityImageObject => {
	const [type, id, widthHeight, format] = ref.split('-');
	const [width, height] = widthHeight.split('x').map(item => parseInt(item));

	if (type !== 'image' || !id || !widthHeight || !format || isNaN(width) || isNaN(height)) {
		throw new Error(`Invalid Sanity image reference: ${ref}`);
	}

	return {
		type,
		id,
		widthHeight,
		format,
		width,
		height
	};
};

/**
 * Creates a Sanity CDN image URL from the given projectId, dataset and image object.
 * @param projectId - The Sanity project ID
 * @param dataset - The Sanity dataset name (e.g., "production").
 * @param img - The image object containing details like ID, dimensions, and format.
 * @see {link: https://www.sanity.io/docs/image-url} for more information
 */
export const createSanityCdnImageUrl = (projectId: string, dataset: string, img: SanityImageObject) => {
	if (!projectId || !dataset || !img) {
		return '';
	}

	const {id, widthHeight, format} = img;

	if (!id || !widthHeight || !format) {
		return '';
	}

	return `https://cdn.sanity.io/images/${projectId}/${dataset}/${id}-${widthHeight}.${format}`;
};

/**
 * Generates query parameters for formatting and resizing a Sanity image.
 * Defaults to a maximum size of 896x896 pixels and automatic format selection.
 * @param conf - Optional configuration for width, height, fit, and format.
 * @returns A query string with the specified or default parameters.
 */
export const sanityFormatAndSizeParameters = (conf?: Partial<SanityFormatAndSizeParametersOptions>) => {
	const options: SanityFormatAndSizeParametersOptions = {
		width: 896,
		height: 896,
		fit: 'max',
		format: 'auto',
		...conf
	};

	const formatParameter = options.format === 'auto' ? 'auto=format' : `fm=${options.format}`;

	return `?${formatParameter}&w=${options.width}&h=${options.height}&fit=${options.fit}`;
};
